import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft,
  ArrowRight, 
  BookOpen, 
  Target,
  Users,
  TrendingUp,
  Database,
  Clock,
  CheckCircle2,
  Sparkles,
  FileText,
  Video,
  MessageCircle
} from "lucide-react";

const modulos = [
  {
    id: 1,
    nome: "Produto & Gestão",
    icon: Target,
    aulas: 8,
    descricao: "Fundamentos, papel do PM, metodologias ágeis, MVP e validação",
    topicos: [
      "O que é um Produto Digital",
      "O papel do Product Manager",
      "Jobs to Be Done",
      "Criando e validando hipóteses (MVPs)",
      "Métodos Ágeis (Scrum/Kanban)"
    ]
  },
  {
    id: 2,
    nome: "UX & Pesquisa",
    icon: Users,
    aulas: 7,
    descricao: "Jornada do usuário, pesquisa qualitativa e quantitativa, prototipação",
    topicos: [
      "Persona e Jornada do Usuário",
      "Identificando Oportunidades",
      "Design Thinking",
      "Métodos de Pesquisa",
      "Prototipação"
    ]
  },
  {
    id: 3,
    nome: "Negócios & Growth",
    icon: TrendingUp,
    aulas: 6,
    descricao: "Modelos de negócio, funil de vendas, CRM e estratégias de crescimento",
    topicos: [
      "Business Model Canvas",
      "Posicionamento e Proposta de Valor",
      "Funil de Vendas e CRM",
      "Estratégias de Growth",
      "Canais de Aquisição"
    ]
  },
  {
    id: 4,
    nome: "Dados & Tech",
    icon: Database,
    aulas: 5,
    descricao: "KPIs, métricas, Google Analytics e stacks de tecnologia",
    topicos: [
      "KPIs e Métricas de Produto",
      "Google Analytics básico",
      "Noções de Stack Tecnológico",
      "Análise de Dados Inicial"
    ]
  }
];

const ferramentas = [
  "Notion", "Google Docs", "Google Forms", "Miro", "Trello", "Padlet", "Mentimeter", "Kahoot"
];

const pilares = [
  { icon: Video, titulo: "Aulas EAD", desc: "26 aulas gravadas para estudar no seu ritmo" },
  { icon: MessageCircle, titulo: "Mentorias ao Vivo", desc: "Encontros para tirar dúvidas e evoluir" },
  { icon: Users, titulo: "Comunidade", desc: "Rede de apoio e troca de experiências" },
  { icon: Target, titulo: "Carreira", desc: "2 mentorias de carreira + suporte WhatsApp" }
];

export default function AlunoCicloBasico() {
  const navigate = useNavigate();

  return (
    <div className="page-container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-4 mb-6"
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/aluno/aprender")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <span className="text-sm text-muted-foreground">Ciclo Básico</span>
      </motion.div>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-primary rounded-2xl p-6 mb-6 text-primary-foreground"
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-primary-foreground/20 flex items-center justify-center shrink-0">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <Badge className="bg-primary-foreground/20 text-primary-foreground mb-2">
              Etapa 1
            </Badge>
            <h1 className="font-heading text-xl font-bold">
              Gestão de Produtos Digitais
            </h1>
            <p className="text-sm opacity-80 mt-1">Ciclo Básico — Introdução</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 text-sm">
          <span className="inline-flex items-center gap-1 bg-primary-foreground/20 px-3 py-1 rounded-full">
            <BookOpen className="w-4 h-4" />
            26 aulas
          </span>
          <span className="inline-flex items-center gap-1 bg-primary-foreground/20 px-3 py-1 rounded-full">
            <Clock className="w-4 h-4" />
            ~13 semanas
          </span>
          <span className="inline-flex items-center gap-1 bg-primary-foreground/20 px-3 py-1 rounded-full">
            4 módulos
          </span>
        </div>
      </motion.div>

      {/* O que você vai aprender */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-6"
      >
        <h2 className="font-heading text-base font-semibold text-foreground mb-3">
          O que você vai aprender
        </h2>
        <div className="space-y-3">
          {modulos.map((modulo, i) => (
            <div key={modulo.id} className="bg-card rounded-2xl p-4 border border-border">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <modulo.icon className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-foreground">{modulo.nome}</h3>
                    <Badge variant="secondary" className="text-[10px]">
                      {modulo.aulas} aulas
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{modulo.descricao}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {modulo.topicos.map((topico, j) => (
                  <span key={j} className="text-[10px] bg-secondary px-2 py-1 rounded-full text-muted-foreground">
                    {topico}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Pilares */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-6"
      >
        <h2 className="font-heading text-base font-semibold text-foreground mb-3">
          Como funciona
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {pilares.map((pilar, i) => (
            <div key={i} className="bg-card rounded-xl p-3 border border-border">
              <pilar.icon className="w-5 h-5 text-primary mb-2" />
              <p className="font-medium text-foreground text-sm">{pilar.titulo}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{pilar.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Ferramentas */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="mb-6"
      >
        <h2 className="font-heading text-base font-semibold text-foreground mb-3">
          Ferramentas que você vai usar
        </h2>
        <div className="flex flex-wrap gap-2">
          {ferramentas.map((ferramenta, i) => (
            <Badge key={i} variant="outline" className="text-xs">
              {ferramenta}
            </Badge>
          ))}
        </div>
      </motion.section>

      {/* Projeto CD */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mb-8"
      >
        <div className="bg-accent/10 rounded-2xl p-5 border border-accent/20">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-foreground">
                Projeto CD — Seu case de portfólio
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Projeto prático em grupo que vira seu primeiro trabalho de portfólio
              </p>
            </div>
          </div>

          <p className="text-sm text-foreground mb-4 font-medium">
            "Isso vira história pra entrevista."
          </p>

          <div className="space-y-2 mb-4">
            <p className="text-xs font-medium text-muted-foreground">Entregáveis finais:</p>
            <div className="flex flex-wrap gap-2">
              {["Case estruturado", "Protótipo", "Análises de usuário", "Proposta de valor", "Evidências de pesquisa", "Modelo de negócio"].map((item, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs bg-card px-2 py-1 rounded-full border border-border">
                  <CheckCircle2 className="w-3 h-3 text-success" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground italic">
            "Você sai com evidência acadêmica e prática, mesmo sem experiência formal."
          </p>
        </div>
      </motion.section>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="sticky bottom-20 bg-background pt-4"
      >
        <Button
          variant="hero"
          size="xl"
          className="w-full"
          onClick={() => navigate("/aluno/aprender/aplicar/basico")}
        >
          Aplicar para o Ciclo Básico
          <ArrowRight className="w-5 h-5" />
        </Button>
      </motion.div>
    </div>
  );
}
